import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CourseSection, LessonItem } from '../../types';
import {
  getSyllabusDB,
  addSectionDB,
  updateSectionDB,
  deleteSectionDB,
  addLessonToSectionDB,
  updateLessonDB,
  deleteLessonFromSectionDB,
  moveLessonDB,
  saveSyllabusDB
} from '../../services/db';
import { Modal } from '../common/Modal';
import { useToast } from '../common/Toast';
import { isSuperAdmin, hasRolePermission, isManagementOrTrainer } from '../../utils/formatters';
import {
  BookOpen,
  Video,
  FileText,
  Plus,
  Edit3,
  Trash2,
  Search,
  ChevronUp,
  ChevronDown,
  ArrowRightLeft,
  Award,
  Layers,
  GripVertical
} from 'lucide-react';

interface DashboardSyllabusManagerProps {
  setActiveTab?: (tab: string) => void;
}

const BENGKUNG_RANK_OPTIONS: { value: CourseSection['bengkungRank']; label: string; color: string }[] = [
  { value: 'hitam', label: 'Bengkung Hitam (Tunas Pendekar)', color: 'bg-slate-900 text-white' },
  { value: 'putih', label: 'Bengkung Putih (Pesilat)', color: 'bg-white text-slate-900 border border-slate-300' },
  { value: 'merah', label: 'Bengkung Merah (Pembantu Jurulatih)', color: 'bg-red-600 text-white' },
  { value: 'kuning', label: 'Bengkung Kuning (Jurulatih)', color: 'bg-[#fff000] text-slate-900' },
];

const LESSON_TYPE_OPTIONS: { value: LessonItem['type']; label: string; icon: React.ReactNode }[] = [
  { value: 'video', label: 'Video Latihan', icon: <Video className="w-3 h-3" /> },
  { value: 'pdf', label: 'Fail Dokumen PDF', icon: <FileText className="w-3 h-3" /> },
  { value: 'reading', label: 'Manual Bacaan', icon: <BookOpen className="w-3 h-3" /> },
];

export const DashboardSyllabusManager: React.FC<DashboardSyllabusManagerProps> = ({ setActiveTab }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [sections, setSections] = useState<CourseSection[]>([]);
  const reorderLockRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState<'sections' | 'lessons'>('sections');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSection, setFilterSection] = useState('Semua');
  const [filterType, setFilterType] = useState('Semua');

  // Section CRUD modals
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<CourseSection | null>(null);
  const [sectionForm, setSectionForm] = useState({
    title: '',
    bengkungRank: 'hitam' as CourseSection['bengkungRank'],
    requiredBengkungName: 'Bengkung Hitam',
    badge: '/badges/hitam0-tunas-pendekar.png',
  });

  // Lesson CRUD modals
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<{ lesson: LessonItem; sectionId: string } | null>(null);
  const [lessonForm, setLessonForm] = useState({
    title: '',
    type: 'video' as LessonItem['type'],
    duration: '10:00',
    description: '',
    remark: '',
    videoUrl: '',
    pdfFileId: '',
    targetSectionId: '',
  });

  // Move lesson modal
  const [isMoveLessonModalOpen, setIsMoveLessonModalOpen] = useState(false);
  const [movingLesson, setMovingLesson] = useState<{ lesson: LessonItem; fromSectionId: string } | null>(null);
  const [moveTargetSectionId, setMoveTargetSectionId] = useState('');

  // Delete confirmation modal
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'section' | 'lesson'; id: string; sectionId?: string; title: string } | null>(null);

  const isManagement = Boolean(currentUser && isManagementOrTrainer(currentUser));

  const loadData = async () => {
    try {
      const data = await getSyllabusDB();
      setSections(data);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => {
      // Ignore generic db_updated events right after a reorder to prevent
      // the background Turso fetch from reverting the optimistic UI state
      if (reorderLockRef.current) return;
      loadData();
    };
    window.addEventListener('asmm_db_updated', handleUpdate);
    window.addEventListener('asmm_syllabus_updated', () => loadData());
    return () => {
      window.removeEventListener('asmm_db_updated', handleUpdate);
      window.removeEventListener('asmm_syllabus_updated', () => loadData());
    };
  }, []);

  // KPI stats
  const allLessons = sections.flatMap(s => s.lessons);
  const videoCount = allLessons.filter(l => l.type === 'video').length;
  const pdfCount = allLessons.filter(l => l.type === 'pdf').length;
  const readingCount = allLessons.filter(l => l.type === 'reading').length;

  // Bengkung rank name mapping
  const rankNameMap: Record<string, string> = {
    hitam: 'Bengkung Hitam',
    putih: 'Bengkung Putih',
    merah: 'Bengkung Merah',
    kuning: 'Bengkung Kuning',
  };

  // Section CRUD handlers
  const openAddSection = () => {
    setEditingSection(null);
    setSectionForm({
      title: '',
      bengkungRank: 'hitam',
      requiredBengkungName: 'Bengkung Hitam',
      badge: '/badges/hitam0-tunas-pendekar.png',
    });
    setIsSectionModalOpen(true);
  };

  const openEditSection = (section: CourseSection) => {
    setEditingSection(section);
    setSectionForm({
      title: section.title,
      bengkungRank: section.bengkungRank,
      requiredBengkungName: section.requiredBengkungName,
      badge: section.badge,
    });
    setIsSectionModalOpen(true);
  };

  const handleSaveSection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sectionForm.title.trim()) {
      showToast('error', 'Sila isi tajuk bahagian.');
      return;
    }

    try {
      if (editingSection) {
        await updateSectionDB(editingSection.id, {
          title: sectionForm.title,
          bengkungRank: sectionForm.bengkungRank,
          requiredBengkungName: sectionForm.requiredBengkungName,
          badge: sectionForm.badge,
        });
        showToast('success', 'Bahagian Dikemaskini!', sectionForm.title);
      } else {
        const newSection: CourseSection = {
          id: `sec-${Date.now()}`,
          title: sectionForm.title,
          bengkungRank: sectionForm.bengkungRank,
          requiredBengkungName: sectionForm.requiredBengkungName,
          badge: sectionForm.badge,
          lessons: [],
        };
        await addSectionDB(newSection);
        showToast('success', 'Bahagian Baru Ditambah!', sectionForm.title);
      }
      setIsSectionModalOpen(false);
      loadData();
    } catch {
      showToast('error', 'Gagal menyimpan bahagian.');
    }
  };

  const handleDeleteSection = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'section') return;
    try {
      await deleteSectionDB(deleteConfirm.id);
      showToast('info', 'Bahagian Dipadam', deleteConfirm.title);
      setDeleteConfirm(null);
      loadData();
    } catch {
      showToast('error', 'Gagal memadam bahagian.');
    }
  };

  const handleMoveSectionUp = (index: number) => {
    if (index === 0) return;
    const reordered = [...sections];
    [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
    // Assign sortOrder based on new position
    reordered.forEach((s, i) => { s.sortOrder = i; });
    setSections(reordered);
    reorderLockRef.current = true;
    saveSyllabusDB(reordered)
      .catch(() => showToast('error', 'Gagal menyusun bahagian.'))
      .finally(() => { setTimeout(() => { reorderLockRef.current = false; }, 6000); });
  };

  const handleMoveSectionDown = (index: number) => {
    if (index >= sections.length - 1) return;
    const reordered = [...sections];
    [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
    reordered.forEach((s, i) => { s.sortOrder = i; });
    setSections(reordered);
    reorderLockRef.current = true;
    saveSyllabusDB(reordered)
      .catch(() => showToast('error', 'Gagal menyusun bahagian.'))
      .finally(() => { setTimeout(() => { reorderLockRef.current = false; }, 6000); });
  };

  // Lesson CRUD handlers
  const openAddLesson = (sectionId?: string) => {
    setEditingLesson(null);
    setLessonForm({
      title: '',
      type: 'video',
      duration: '10:00',
      description: '',
      remark: '',
      videoUrl: '',
      pdfFileId: '',
      targetSectionId: sectionId || (sections.length > 0 ? sections[0].id : ''),
    });
    setIsLessonModalOpen(true);
  };

  const openEditLesson = (lesson: LessonItem, sectionId: string) => {
    setEditingLesson({ lesson, sectionId });
    setLessonForm({
      title: lesson.title,
      type: lesson.type,
      duration: lesson.duration,
      description: lesson.description,
      remark: lesson.remark || '',
      videoUrl: lesson.videoUrl || '',
      pdfFileId: lesson.pdfFileId || '',
      targetSectionId: sectionId,
    });
    setIsLessonModalOpen(true);
  };

  const handleSaveLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lessonForm.title.trim() || !lessonForm.description.trim()) {
      showToast('error', 'Sila isi tajuk dan penerangan pelajaran.');
      return;
    }
    if (!lessonForm.targetSectionId) {
      showToast('error', 'Sila pilih bahagian sasaran.');
      return;
    }

    try {
      if (editingLesson) {
        // If section changed, move it
        if (editingLesson.sectionId !== lessonForm.targetSectionId) {
          await moveLessonDB(editingLesson.sectionId, lessonForm.targetSectionId, editingLesson.lesson.id);
          // After moving, update lesson data in new section
          await updateLessonDB(lessonForm.targetSectionId, editingLesson.lesson.id, {
            title: lessonForm.title,
            type: lessonForm.type,
            duration: lessonForm.type === 'pdf' ? 'Fail PDF' : lessonForm.duration,
            description: lessonForm.description,
            remark: lessonForm.remark.trim() || undefined,
            videoUrl: lessonForm.videoUrl || undefined,
            pdfFileId: lessonForm.pdfFileId || undefined,
          });
        } else {
          await updateLessonDB(editingLesson.sectionId, editingLesson.lesson.id, {
            title: lessonForm.title,
            type: lessonForm.type,
            duration: lessonForm.type === 'pdf' ? 'Fail PDF' : lessonForm.duration,
            description: lessonForm.description,
            remark: lessonForm.remark.trim() || undefined,
            videoUrl: lessonForm.videoUrl || undefined,
            pdfFileId: lessonForm.pdfFileId || undefined,
          });
        }
        showToast('success', 'Pelajaran Dikemaskini!', lessonForm.title);
      } else {
        const newLesson: LessonItem = {
          id: `les-${Date.now()}`,
          title: lessonForm.title,
          type: lessonForm.type,
          duration: lessonForm.type === 'pdf' ? 'Fail PDF' : lessonForm.duration,
          completed: false,
          description: lessonForm.description,
          remark: lessonForm.remark.trim() || undefined,
          videoUrl: lessonForm.videoUrl || undefined,
          pdfFileId: lessonForm.pdfFileId || undefined,
        };
        await addLessonToSectionDB(lessonForm.targetSectionId, newLesson);
        showToast('success', 'Pelajaran Baru Ditambah!', lessonForm.title);
      }
      setIsLessonModalOpen(false);
      loadData();
    } catch {
      showToast('error', 'Gagal menyimpan pelajaran.');
    }
  };

  const handleDeleteLesson = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'lesson' || !deleteConfirm.sectionId) return;
    try {
      await deleteLessonFromSectionDB(deleteConfirm.sectionId, deleteConfirm.id);
      showToast('info', 'Pelajaran Dipadam', deleteConfirm.title);
      setDeleteConfirm(null);
      loadData();
    } catch {
      showToast('error', 'Gagal memadam pelajaran.');
    }
  };

  const handleMoveLessonUp = (sectionId: string, lessonIndex: number) => {
    const secIdx = sections.findIndex(s => s.id === sectionId);
    if (secIdx < 0 || lessonIndex === 0) return;
    const reordered = [...sections];
    const lessons = [...reordered[secIdx].lessons];
    [lessons[lessonIndex - 1], lessons[lessonIndex]] = [lessons[lessonIndex], lessons[lessonIndex - 1]];
    lessons.forEach((l, i) => { l.sortOrder = i; });
    reordered[secIdx] = { ...reordered[secIdx], lessons };
    setSections(reordered);
    reorderLockRef.current = true;
    saveSyllabusDB(reordered)
      .catch(() => showToast('error', 'Gagal menyusun pelajaran.'))
      .finally(() => { setTimeout(() => { reorderLockRef.current = false; }, 6000); });
  };

  const handleMoveLessonDown = (sectionId: string, lessonIndex: number) => {
    const secIdx = sections.findIndex(s => s.id === sectionId);
    if (secIdx < 0) return;
    const sec = sections[secIdx];
    if (lessonIndex >= sec.lessons.length - 1) return;
    const reordered = [...sections];
    const lessons = [...reordered[secIdx].lessons];
    [lessons[lessonIndex], lessons[lessonIndex + 1]] = [lessons[lessonIndex + 1], lessons[lessonIndex]];
    lessons.forEach((l, i) => { l.sortOrder = i; });
    reordered[secIdx] = { ...reordered[secIdx], lessons };
    setSections(reordered);
    reorderLockRef.current = true;
    saveSyllabusDB(reordered)
      .catch(() => showToast('error', 'Gagal menyusun pelajaran.'))
      .finally(() => { setTimeout(() => { reorderLockRef.current = false; }, 6000); });
  };

  const openMoveLesson = (lesson: LessonItem, fromSectionId: string) => {
    setMovingLesson({ lesson, fromSectionId });
    setMoveTargetSectionId(sections.find(s => s.id !== fromSectionId)?.id || '');
    setIsMoveLessonModalOpen(true);
  };

  const handleMoveLesson = async () => {
    if (!movingLesson || !moveTargetSectionId) return;
    try {
      await moveLessonDB(movingLesson.fromSectionId, moveTargetSectionId, movingLesson.lesson.id);
      const targetName = sections.find(s => s.id === moveTargetSectionId)?.title || '';
      showToast('success', 'Pelajaran Dipindahkan!', `${movingLesson.lesson.title} → ${targetName}`);
      setIsMoveLessonModalOpen(false);
      setMovingLesson(null);
      loadData();
    } catch {
      showToast('error', 'Gagal memindahkan pelajaran.');
    }
  };

  // Filter lessons for the lessons tab
  const filteredLessons: { lesson: LessonItem; sectionId: string; sectionTitle: string; lessonIndex: number }[] = [];
  sections.forEach(sec => {
    sec.lessons.forEach((les, idx) => {
      const matchSection = filterSection === 'Semua' || sec.id === filterSection;
      const matchType = filterType === 'Semua' || les.type === filterType;
      const matchSearch =
        les.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        les.description.toLowerCase().includes(searchQuery.toLowerCase());
      if (matchSection && matchType && matchSearch) {
        filteredLessons.push({ lesson: les, sectionId: sec.id, sectionTitle: sec.title, lessonIndex: idx });
      }
    });
  });

  const getLessonTypeIcon = (type: string) => {
    if (type === 'video') return <Video className="w-3.5 h-3.5 text-blue-600" />;
    if (type === 'pdf') return <FileText className="w-3.5 h-3.5 text-amber-600" />;
    return <BookOpen className="w-3.5 h-3.5 text-emerald-600" />;
  };

  const getLessonTypeBadge = (type: string) => {
    if (type === 'video') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (type === 'pdf') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  const getRankBadgeClass = (rank: string) => {
    if (rank === 'hitam') return 'bg-slate-900 text-white';
    if (rank === 'putih') return 'bg-white text-slate-900 border border-slate-300';
    if (rank === 'merah') return 'bg-red-600 text-white';
    if (rank === 'kuning') return 'bg-[#fff000] text-slate-900';
    return 'bg-slate-100 text-slate-700';
  };

  if (!isManagement) return null;

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-red-50 text-asmm-red rounded-xl">
              <BookOpen className="w-5 h-5" />
            </span>
            <h3 className="text-base font-extrabold text-slate-900">
              Pengurusan Kurikulum & Silibus
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Tambah, kemaskini, susun semula dan padam bahagian serta pelajaran silibus ASMM.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openAddLesson()}
            className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah Pelajaran</span>
          </button>

          <button
            type="button"
            onClick={openAddSection}
            className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 cursor-pointer transition-all"
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Tambah Bahagian</span>
          </button>
        </div>
      </div>

      {/* KPI Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Bahagian</div>
          <div className="text-base sm:text-lg font-black text-slate-900 font-mono">{sections.length}</div>
        </div>
        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pelajaran</div>
          <div className="text-base sm:text-lg font-black text-asmm-red font-mono">{allLessons.length}</div>
        </div>
        <div className="p-3.5 bg-blue-50/50 rounded-2xl border border-blue-200/50 space-y-0.5">
          <div className="text-[10px] font-bold text-blue-500 uppercase tracking-wider flex items-center gap-1"><Video className="w-3 h-3" /> Video</div>
          <div className="text-base sm:text-lg font-black text-blue-700 font-mono">{videoCount}</div>
        </div>
        <div className="p-3.5 bg-amber-50/50 rounded-2xl border border-amber-200/50 space-y-0.5">
          <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1"><FileText className="w-3 h-3" /> PDF</div>
          <div className="text-base sm:text-lg font-black text-amber-700 font-mono">{pdfCount}</div>
        </div>
        <div className="p-3.5 bg-emerald-50/50 rounded-2xl border border-emerald-200/50 space-y-0.5">
          <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1"><BookOpen className="w-3 h-3" /> Bacaan</div>
          <div className="text-base sm:text-lg font-black text-emerald-700 font-mono">{readingCount}</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveSubTab('sections')}
            className={`px-3.5 py-1.5 rounded-xl font-extrabold text-xs transition-all cursor-pointer ${
              activeSubTab === 'sections'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Peringkat Bengkung ({sections.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('lessons')}
            className={`px-3.5 py-1.5 rounded-xl font-extrabold text-xs transition-all cursor-pointer ${
              activeSubTab === 'lessons'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Semua Pelajaran ({allLessons.length})
          </button>
        </div>
      </div>

      {/* ============================== */}
      {/* SUB-TAB 1: SECTIONS MANAGEMENT */}
      {/* ============================== */}
      {activeSubTab === 'sections' && (
        <div className="space-y-3">
          {sections.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-bold">Tiada bahagian silibus.</p>
              <p className="text-xs mt-1">Klik "Tambah Bahagian" untuk mencipta peringkat bengkung pertama.</p>
            </div>
          ) : (
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                    <th className="py-2.5 px-3 w-8">#</th>
                    <th className="py-2.5 px-3">Peringkat Bengkung</th>
                    <th className="py-2.5 px-3">Peringkat</th>
                    <th className="py-2.5 px-3 text-center">Pelajaran</th>
                    <th className="py-2.5 px-3 text-center">Susun</th>
                    <th className="py-2.5 px-3 text-right">Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, idx) => (
                    <tr key={section.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-3 text-slate-400 font-mono text-[10px]">{idx + 1}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2.5">
                          {section.badge && (
                            <img src={section.badge} alt="" className="w-7 h-7 object-contain shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 truncate">{section.title}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{section.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold ${getRankBadgeClass(section.bengkungRank)}`}>
                          {section.requiredBengkungName}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="font-mono font-bold text-slate-700">{section.lessons.length}</span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => handleMoveSectionUp(idx)}
                            disabled={idx === 0}
                            className={`p-1 rounded-lg transition-all cursor-pointer ${idx === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveSectionDown(idx)}
                            disabled={idx >= sections.length - 1}
                            className={`p-1 rounded-lg transition-all cursor-pointer ${idx >= sections.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openAddLesson(section.id)}
                            className="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 transition-all cursor-pointer"
                            title="Tambah Pelajaran ke Bahagian Ini"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditSection(section)}
                            className="p-1.5 rounded-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all cursor-pointer"
                            title="Kemaskini Bahagian"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm({ type: 'section', id: section.id, title: section.title })}
                            className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-all cursor-pointer"
                            title="Padam Bahagian"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================== */}
      {/* SUB-TAB 2: LESSONS MANAGEMENT */}
      {/* ============================== */}
      {activeSubTab === 'lessons' && (
        <div className="space-y-3">
          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <div className="relative flex-1 w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari tajuk atau penerangan pelajaran..."
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>
            <select
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
              className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900 shrink-0"
            >
              <option value="Semua">Semua Bahagian</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900 shrink-0"
            >
              <option value="Semua">Semua Jenis</option>
              <option value="video">Video</option>
              <option value="pdf">PDF</option>
              <option value="reading">Bacaan</option>
            </select>
          </div>

          {filteredLessons.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-bold">Tiada pelajaran ditemui.</p>
              <p className="text-xs mt-1">Cuba tukar carian atau penapis anda.</p>
            </div>
          ) : (
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                    <th className="py-2.5 px-3">Pelajaran</th>
                    <th className="py-2.5 px-3">Bahagian</th>
                    <th className="py-2.5 px-3">Jenis</th>
                    <th className="py-2.5 px-3">Durasi</th>
                    <th className="py-2.5 px-3 text-center">Susun</th>
                    <th className="py-2.5 px-3 text-right">Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLessons.map(({ lesson, sectionId, sectionTitle, lessonIndex }) => {
                    const section = sections.find(s => s.id === sectionId);
                    return (
                      <tr key={`${sectionId}-${lesson.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                        <td className="py-3 px-3">
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900 truncate max-w-[240px]">{lesson.title}</div>
                            <div className="text-[10px] text-slate-500 truncate max-w-[240px]">{lesson.description}</div>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold ${getRankBadgeClass(section?.bengkungRank || '')}`}>
                            {section?.requiredBengkungName || sectionTitle}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${getLessonTypeBadge(lesson.type)}`}>
                            {getLessonTypeIcon(lesson.type)}
                            {lesson.type === 'video' ? 'Video' : lesson.type === 'pdf' ? 'PDF' : 'Bacaan'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="font-mono text-slate-600">{lesson.duration}</span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleMoveLessonUp(sectionId, lessonIndex)}
                              disabled={lessonIndex === 0}
                              className={`p-1 rounded-lg transition-all cursor-pointer ${lessonIndex === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveLessonDown(sectionId, lessonIndex)}
                              disabled={!section || lessonIndex >= section.lessons.length - 1}
                              className={`p-1 rounded-lg transition-all cursor-pointer ${!section || lessonIndex >= section.lessons.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openMoveLesson(lesson, sectionId)}
                              className="p-1.5 rounded-lg text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-all cursor-pointer"
                              title="Pindahkan ke Bahagian Lain"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditLesson(lesson, sectionId)}
                              className="p-1.5 rounded-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all cursor-pointer"
                              title="Kemaskini Pelajaran"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm({ type: 'lesson', id: lesson.id, sectionId, title: lesson.title })}
                              className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-all cursor-pointer"
                              title="Padam Pelajaran"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================== */}
      {/* SECTION CREATE/EDIT MODAL      */}
      {/* ============================== */}
      {isSectionModalOpen && (
        <Modal
          isOpen={isSectionModalOpen}
          onClose={() => setIsSectionModalOpen(false)}
          title={editingSection ? 'Kemaskini Bahagian Silibus' : 'Tambah Bahagian Silibus Baru'}
        >
          <form onSubmit={handleSaveSection} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Nama Peringkat Bengkung *</label>
              <input
                type="text"
                required
                value={sectionForm.title}
                onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Contoh: Bengkung Biru Samudera"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Peringkat Bengkung</label>
                <select
                  value={sectionForm.bengkungRank}
                  onChange={(e) => {
                    const rank = e.target.value as CourseSection['bengkungRank'];
                    setSectionForm(prev => ({
                      ...prev,
                      bengkungRank: rank,
                      requiredBengkungName: rankNameMap[rank] || rank,
                    }));
                  }}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  {BENGKUNG_RANK_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nama Keperluan Bengkung</label>
                <input
                  type="text"
                  value={sectionForm.requiredBengkungName}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, requiredBengkungName: e.target.value }))}
                  placeholder="Contoh: Bengkung Hitam"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">URL Badge / Ikon</label>
              <input
                type="text"
                value={sectionForm.badge}
                onChange={(e) => setSectionForm(prev => ({ ...prev, badge: e.target.value }))}
                placeholder="/badges/hitam0-tunas-pendekar.png"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
              />
              {sectionForm.badge && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={sectionForm.badge} alt="Preview" className="w-8 h-8 object-contain border border-slate-200 rounded-lg p-0.5" />
                  <span className="text-[10px] text-slate-500">Pratonton Badge</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsSectionModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Award className="w-3.5 h-3.5" />
                <span>{editingSection ? 'Kemaskini Bahagian' : 'Simpan Bahagian'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ============================== */}
      {/* LESSON CREATE/EDIT MODAL       */}
      {/* ============================== */}
      {isLessonModalOpen && (
        <Modal
          isOpen={isLessonModalOpen}
          onClose={() => setIsLessonModalOpen(false)}
          title={editingLesson ? 'Kemaskini Pelajaran' : 'Tambah Pelajaran Baru'}
        >
          <form onSubmit={handleSaveLesson} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Pelajaran *</label>
              <input
                type="text"
                required
                value={lessonForm.title}
                onChange={(e) => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Contoh: Jurus 6 & 7 Awang Sulung"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Bahagian Sasaran *</label>
                <select
                  value={lessonForm.targetSectionId}
                  onChange={(e) => setLessonForm(prev => ({ ...prev, targetSectionId: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  {sections.length === 0 && <option value="">Tiada bahagian</option>}
                  {sections.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Kandungan</label>
                <select
                  value={lessonForm.type}
                  onChange={(e) => setLessonForm(prev => ({ ...prev, type: e.target.value as LessonItem['type'] }))}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
                >
                  {LESSON_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {lessonForm.type !== 'pdf' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Durasi / Masa Pelajaran</label>
                <input
                  type="text"
                  value={lessonForm.duration}
                  onChange={(e) => setLessonForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="Contoh: 15:30 atau 05:00"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Penerangan / Falsafah Pelajaran *</label>
              <textarea
                required
                rows={3}
                value={lessonForm.description}
                onChange={(e) => setLessonForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Penerangan terperinci sukatan pelajaran atau buah kuncian..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Remark / Nota Panduan Tajuk (Pilihan)
              </label>
              <input
                type="text"
                value={lessonForm.remark}
                onChange={(e) => setLessonForm(prev => ({ ...prev, remark: e.target.value }))}
                placeholder="Contoh: Fokus kepada kekuda rendah, elak hayun tangan berlebihan..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900"
              />
              <p className="text-[10px] text-slate-400 mt-1">Remark atau tips khusus bagi tajuk sukatan ini yang dipaparkan kepada pesilat &amp; jurulatih.</p>
            </div>

            {/* Video URL field (shown for video type) */}
            {lessonForm.type === 'video' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">URL Video Latihan</label>
                <input
                  type="url"
                  value={lessonForm.videoUrl}
                  onChange={(e) => setLessonForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                  placeholder="https://drive.google.com/... atau URL video lain"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
                <p className="text-[10px] text-slate-400 mt-1">Masukkan URL langsung ke fail video. Biarkan kosong untuk video contoh.</p>
              </div>
            )}

            {/* PDF File ID field (shown for pdf type) */}
            {lessonForm.type === 'pdf' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">ID Fail Google Drive (PDF)</label>
                <input
                  type="text"
                  value={lessonForm.pdfFileId}
                  onChange={(e) => setLessonForm(prev => ({ ...prev, pdfFileId: e.target.value }))}
                  placeholder="gdrive-doc-03 atau ID Fail Drive"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono"
                />
                <p className="text-[10px] text-slate-400 mt-1">ID rujukan dokumen Google Drive dari sistem ASMM.</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsLessonModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <BookOpen className="w-3.5 h-3.5" />
                <span>{editingLesson ? 'Kemaskini Pelajaran' : 'Simpan Pelajaran'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ============================== */}
      {/* MOVE LESSON MODAL              */}
      {/* ============================== */}
      {isMoveLessonModalOpen && movingLesson && (
        <Modal
          isOpen={isMoveLessonModalOpen}
          onClose={() => { setIsMoveLessonModalOpen(false); setMovingLesson(null); }}
          title="Pindahkan Pelajaran ke Bahagian Lain"
        >
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Pelajaran:</div>
              <div className="text-sm font-bold text-slate-900">{movingLesson.lesson.title}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Dari: <strong>{sections.find(s => s.id === movingLesson.fromSectionId)?.title}</strong>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Bahagian Sasaran Baru</label>
              <select
                value={moveTargetSectionId}
                onChange={(e) => setMoveTargetSectionId(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none text-xs text-slate-900"
              >
                {sections
                  .filter(s => s.id !== movingLesson.fromSectionId)
                  .map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))
                }
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setIsMoveLessonModalOpen(false); setMovingLesson(null); }}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleMoveLesson}
                disabled={!moveTargetSectionId}
                className="px-5 py-2 bg-purple-600 text-white font-bold text-xs rounded-xl hover:bg-purple-700 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                <span>Pindahkan Pelajaran</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============================== */}
      {/* DELETE CONFIRMATION MODAL       */}
      {/* ============================== */}
      {deleteConfirm && (
        <Modal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title={`Sahkan Pemadaman ${deleteConfirm.type === 'section' ? 'Bahagian' : 'Pelajaran'}`}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Adakah anda pasti ingin memadam {deleteConfirm.type === 'section' ? 'bahagian' : 'pelajaran'}{' '}
              <strong className="text-slate-900">{deleteConfirm.title}</strong>?
            </p>
            {deleteConfirm.type === 'section' && (
              <p className="text-xs text-amber-600 font-medium bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                ⚠️ Semua pelajaran di dalam bahagian ini juga akan dipadam.
              </p>
            )}
            <p className="text-xs text-red-600 font-medium">Tindakan ini tidak boleh dibatalkan.</p>

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => deleteConfirm.type === 'section' ? handleDeleteSection() : handleDeleteLesson()}
                className="px-5 py-2 bg-red-600 text-white font-bold text-xs rounded-xl hover:bg-red-700 transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Padam {deleteConfirm.type === 'section' ? 'Bahagian' : 'Pelajaran'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
